import { useEffect } from 'react';
import { useAuth } from '@/lib/AuthContext';
import { base44 } from '@/api/base44Client';
import { safeReturnUrl } from '@/lib/trustedOrigins';

function AdminNoIndex() {
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"][data-admin="true"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'robots';
      meta.setAttribute('data-admin', 'true');
      document.head.appendChild(meta);
    }
    meta.content = 'noindex, nofollow';
    return () => { if (document.head.contains(meta)) document.head.removeChild(meta); };
  }, []);
  return null;
}

export default function AdminGuard({ children }) {
  const { user, isLoadingAuth } = useAuth();

  // Public-domain admin URLs are also blocked at the edge. Handle SPA navigation
  // here so no admin component renders even for an authenticated browser.
  const isPublicSite = /^(www\.)?trinitydentalarts\.com$/i.test(window.location.hostname);
  if (isPublicSite) {
    return (
      <>
        <AdminNoIndex />
        <main className="mx-auto max-w-2xl px-6 py-24">
          <h1 className="text-3xl font-semibold">We could not find that page</h1>
          <p className="mt-4"><a href="/">Return to Trinity Dental Arts</a></p>
        </main>
      </>
    );
  }

  // noindex is injected on EVERY guard state (loading, redirect, granted) so a
  // JS-rendering crawler never sees an /admin/ route without it — not only on
  // the admin-granted render path.
  if (isLoadingAuth) {
    return (
      <>
        <AdminNoIndex />
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
        </div>
      </>
    );
  }

  if (!user || user.role !== 'admin') {
    base44.auth.redirectToLogin(safeReturnUrl(window.location.pathname, '/'));
    return <AdminNoIndex />;
  }

  return <><AdminNoIndex />{children}</>;
}